<template>
  <div class="client-onboarding-page">
    <div class="container py-2">
      <!-- Loading -->
      <div v-if="loading" class="text-center my-5">
        <b-spinner class="align-middle mr-1" />
        <strong>Loading…</strong>
      </div>

      <!-- Invalid link -->
      <b-card v-else-if="notFound" class="text-center hero-card">
        <img src="/logo.png" alt="Swiss Finance Corporation" class="hero-logo mb-2" />
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-warning mb-1" />
        <h3>This link is not valid</h3>
        <p class="text-muted mb-0">
          The onboarding link you followed is invalid or has been revoked.
          Please contact Swiss Finance Corporation for a new link.
        </p>
      </b-card>

      <template v-else>
        <!-- Header -->
        <div class="hero-card card mb-2">
          <div class="hero-accent"></div>
          <div class="card-body text-center">
            <img src="/logo.png" alt="Swiss Finance Corporation" class="hero-logo mb-1" />
            <h3 class="hero-title mb-2">Professional Client Agreement</h3>
            <div class="hero-chips">
              <span class="hero-chip">
                <feather-icon icon="BriefcaseIcon" size="14" class="mr-25" />
                {{ accountName }}<span v-if="accountNumber" class="chip-muted">&nbsp;· {{ accountNumber }}</span>
              </span>
              <span class="hero-chip">
                <feather-icon icon="UserIcon" size="14" class="mr-25" />
                {{ contactName }}
              </span>
              <span class="hero-chip" :class="signed ? 'chip-success' : 'chip-pending'">
                <feather-icon :icon="signed ? 'CheckCircleIcon' : 'Edit3Icon'" size="14" class="mr-25" />
                {{ signed ? "Signed" : "Awaiting completion" }}
              </span>
            </div>
          </div>
        </div>

        <b-alert v-if="signed" show variant="success" class="p-1">
          <feather-icon icon="CheckCircleIcon" class="mr-50" />
          This form was signed by <strong>{{ signatureName }}</strong> on <strong>{{ signedDate }}</strong>.
          The form can no longer be changed, but you can still upload and view documents below.
        </b-alert>

        <b-alert v-if="saveError" show variant="danger" class="p-1">{{ saveError }}</b-alert>

        <!-- ============================ SECTION A ============================ -->
        <div class="pca-form">
          <validation-observer ref="formValidation">
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">A</span>
                <div>
                  <h4 class="mb-0">Client Information</h4>
                  <small class="text-muted">General details about the account holder</small>
                </div>
              </div>
              <b-card-body>
            <fieldset :disabled="signed">
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Account Name *">
                    <validation-provider #default="{ errors }" rules="required" name="Account Name">
                      <b-form-input v-model="form.sectionA.accountName" :state="errors.length ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Client Type *">
                    <validation-provider #default="{ errors }" rules="required" name="Client Type">
                      <b-form-radio-group
                        v-model="form.sectionA.clientType"
                        :options="['Company', 'Individual']"
                        :state="errors.length ? false : null"
                        class="mt-50"
                      />
                      <small class="text-danger d-block">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group label="Address (of Registered Office if company) *">
                <validation-provider #default="{ errors }" rules="required" name="Address">
                  <b-form-textarea v-model="form.sectionA.address" rows="2" :state="errors.length ? false : null" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label="Name and address required on contracts and statements (if different from above)">
                <b-form-textarea v-model="form.sectionA.contractsNameAddress" rows="2" />
              </b-form-group>

              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Required frequency of daily reports">
                    <b-form-input v-model="form.sectionA.reportFrequency" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <b-form-group label="Form of reports required">
                    <b-form-radio-group
                      v-model="form.sectionA.reportForm"
                      :options="['fax', 'e-mail', 'mail', 'other']"
                      class="mt-50"
                    />
                    <b-form-input
                      v-if="form.sectionA.reportForm === 'other'"
                      v-model="form.sectionA.reportFormOther"
                      placeholder="Please specify"
                      class="mt-50"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="12" md="4">
                  <b-form-group label="Company registration number (if applicable)">
                    <b-form-input v-model="form.sectionA.companyRegNumber" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="Country tax domiciled *">
                    <validation-provider #default="{ errors }" rules="required" name="Country Tax Domiciled">
                      <b-form-input v-model="form.sectionA.taxCountry" :state="errors.length ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="Nature of business">
                    <b-form-input v-model="form.sectionA.natureOfBusiness" />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group label="Name and address of bankers">
                <b-form-textarea v-model="form.sectionA.bankersNameAddress" rows="2" />
              </b-form-group>

              <h6 class="mt-1">Contact details</h6>
              <b-row>
                <b-col cols="6" md="3">
                  <b-form-group label="Tel / Home (if applicable)">
                    <b-form-input v-model="form.sectionA.telHome" />
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="3">
                  <b-form-group label="Tel / Office">
                    <b-form-input v-model="form.sectionA.telOffice" />
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="2">
                  <b-form-group label="Mobile">
                    <b-form-input v-model="form.sectionA.mobile" />
                  </b-form-group>
                </b-col>
                <b-col cols="6" md="2">
                  <b-form-group label="Fax">
                    <b-form-input v-model="form.sectionA.fax" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="2">
                  <b-form-group label="Email *">
                    <validation-provider #default="{ errors }" rules="required|email" name="Email">
                      <b-form-input v-model="form.sectionA.email" :state="errors.length ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <h6 class="mt-1">Bank details</h6>
              <b-row>
                <b-col cols="12" md="4">
                  <b-form-group label="Sort code / SWIFT address">
                    <b-form-input v-model="form.sectionA.sortCodeSwift" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="Account name">
                    <b-form-input v-model="form.sectionA.bankAccountName" />
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="4">
                  <b-form-group label="Account number">
                    <b-form-input v-model="form.sectionA.bankAccountNumber" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Name of officer">
                <b-form-input v-model="form.sectionA.officerName" />
              </b-form-group>
            </fieldset>

              <!-- person lists sit outside the fieldset so their document uploads stay
                   available once the form has been signed -->
              <h6 class="mt-1">Persons / traders authorised to give instructions</h6>
              <div v-for="(trader, index) in form.sectionA.authorisedTraders" :key="'at' + index" class="person-block">
                <fieldset :disabled="signed">
                  <b-row class="mb-50">
                    <b-col>
                      <b-form-input v-model="trader.name" placeholder="Name of trader" />
                    </b-col>
                    <b-col cols="auto">
                      <b-button variant="flat-danger" :disabled="signed" @click="form.sectionA.authorisedTraders.splice(index, 1)">
                        <feather-icon icon="Trash2Icon" size="14" />
                      </b-button>
                    </b-col>
                  </b-row>
                </fieldset>

                <div v-if="authorisedTraderDocumentRules.length" class="person-docs">
                  <div
                    v-for="rule in authorisedTraderDocumentRules"
                    :key="'atdoc' + index + '-' + rule.id"
                    class="doc-slot"
                  >
                    <div class="doc-slot-info">
                      <feather-icon
                        :icon="uploadedFor(rule.id, 'AuthorisedTrader', index) ? 'CheckCircleIcon' : 'CircleIcon'"
                        :class="uploadedFor(rule.id, 'AuthorisedTrader', index) ? 'text-success' : 'text-muted'"
                        size="14"
                        class="mr-50"
                      />
                      <span>{{ rule.Name }}</span>
                      <small v-if="uploadedFor(rule.id, 'AuthorisedTrader', index)" class="doc-slot-file">
                        {{ uploadedFor(rule.id, "AuthorisedTrader", index).FileName }}
                      </small>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="uploading"
                      @click="pickRuleFile(rule, 'AuthorisedTrader', index, trader.name)"
                    >
                      <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                      {{ uploadedFor(rule.id, "AuthorisedTrader", index) ? "Replace" : "Upload" }}
                    </b-button>
                  </div>
                </div>
              </div>
              <b-button variant="flat-primary" size="sm" :disabled="signed" @click="form.sectionA.authorisedTraders.push({ name: '' })">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Add trader
              </b-button>

              <h6 class="mt-1">Signatories authorised to instruct non-dealing matters</h6>
              <div v-for="(signatory, index) in form.sectionA.nonDealingSignatories" :key="'nd' + index" class="person-block">
                <fieldset :disabled="signed">
                  <b-row class="mb-50">
                    <b-col>
                      <b-form-input v-model="signatory.name" placeholder="Name" />
                    </b-col>
                    <b-col cols="auto">
                      <b-button variant="flat-danger" :disabled="signed" @click="form.sectionA.nonDealingSignatories.splice(index, 1)">
                        <feather-icon icon="Trash2Icon" size="14" />
                      </b-button>
                    </b-col>
                  </b-row>
                </fieldset>

                <div v-if="signatoryDocumentRules.length" class="person-docs">
                  <div
                    v-for="rule in signatoryDocumentRules"
                    :key="'nddoc' + index + '-' + rule.id"
                    class="doc-slot"
                  >
                    <div class="doc-slot-info">
                      <feather-icon
                        :icon="uploadedFor(rule.id, 'NonDealingSignatory', index) ? 'CheckCircleIcon' : 'CircleIcon'"
                        :class="uploadedFor(rule.id, 'NonDealingSignatory', index) ? 'text-success' : 'text-muted'"
                        size="14"
                        class="mr-50"
                      />
                      <span>{{ rule.Name }}</span>
                      <small v-if="uploadedFor(rule.id, 'NonDealingSignatory', index)" class="doc-slot-file">
                        {{ uploadedFor(rule.id, "NonDealingSignatory", index).FileName }}
                      </small>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="uploading"
                      @click="pickRuleFile(rule, 'NonDealingSignatory', index, signatory.name)"
                    >
                      <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                      {{ uploadedFor(rule.id, "NonDealingSignatory", index) ? "Replace" : "Upload" }}
                    </b-button>
                  </div>
                </div>
              </div>
              <b-button variant="flat-primary" size="sm" :disabled="signed" @click="form.sectionA.nonDealingSignatories.push({ name: '' })">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Add signatory
              </b-button>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION B ============================ -->
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">B</span>
                <div>
                  <h4 class="mb-0">Knowledge and Experience Information</h4>
                  <small class="text-muted">So we can ascertain whether it is appropriate to categorise you as a Professional Client</small>
                </div>
              </div>
              <b-card-body>
              <fieldset :disabled="signed">
                <b-form-group :label="'1. Number of years of experience you have had in the markets in which you wish to trade' + (sectionBRules ? ' *' : '')">
                  <validation-provider #default="{ errors }" :rules="sectionBRules" name="Years of Experience">
                    <b-form-input v-model="form.sectionB.yearsExperience" :state="errors.length ? false : null" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group :label="'2. Overall frequency of your previous dealings during both the past 12 months and the period stated above' + (sectionBRules ? ' *' : '')">
                  <validation-provider #default="{ errors }" :rules="sectionBRules" name="Frequency of Dealings">
                    <b-form-input v-model="form.sectionB.dealingFrequency" :state="errors.length ? false : null" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group :label="'3. Typical size of transactions initiated by yourself in these markets' + (sectionBRules ? ' *' : '')">
                  <validation-provider #default="{ errors }" :rules="sectionBRules" name="Typical Size">
                    <b-form-input v-model="form.sectionB.typicalSize" :state="errors.length ? false : null" />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group :label="'4. In what capacity have you been active in these markets?' + (sectionBRules ? ' *' : '')">
                  <validation-provider #default="{ errors }" :rules="sectionBRules" name="Capacity">
                    <b-form-radio-group
                      v-model="form.sectionB.capacity"
                      :options="['Advisory', 'Execution only', 'Discretion given to third party']"
                    />
                    <small class="text-danger d-block">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
                <b-form-group label="5. Are your trading activities performed on a long term or a speculative or hedging basis?">
                  <b-form-input v-model="form.sectionB.tradingBasis" />
                </b-form-group>
                <b-form-group label="6. Details of any other experience in any other financial market">
                  <b-form-textarea v-model="form.sectionB.otherExperience" rows="2" />
                </b-form-group>
                <b-form-group :label="'7. On the basis of your knowledge and experience, do you understand the risks of dealing in these markets and the potential for loss of capital in excess of your original investment?' + (sectionBRules ? ' *' : '')">
                  <validation-provider #default="{ errors }" :rules="sectionBRules" name="Risk Understanding">
                    <b-form-radio-group v-model="form.sectionB.understandsRisks" :options="['Yes', 'No']" />
                    <small class="text-danger d-block">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </fieldset>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION C ============================ -->
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">C</span>
                <div>
                  <h4 class="mb-0">Loss of Protection Notice for Professional Clients</h4>
                  <small class="text-muted">Please read the notice below carefully</small>
                </div>
              </div>
              <b-card-body>
                <div class="notice-text mb-1">{{ sectionCText }}</div>
                <b-form-checkbox v-model="form.sectionC.acknowledged" :disabled="signed" class="ack-check">
                  I have read and understood the Loss of Protection Notice for Professional Clients
                </b-form-checkbox>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION D ============================ -->
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">D</span>
                <div>
                  <h4 class="mb-0">Terms of Business</h4>
                  <small class="text-muted">The terms on which Swiss Finance will carry out trading activities for you</small>
                </div>
              </div>
              <b-card-body>
                <div class="notice-text mb-1">{{ sectionDText }}</div>
                <b-form-checkbox v-model="form.sectionD.acknowledged" :disabled="signed" class="ack-check">
                  I have read and understood the Terms of Business, including the Conflict of Interest Policy
                  and the Best Execution Policy
                </b-form-checkbox>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION F (choice) ============================ -->
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">F</span>
                <div>
                  <h4 class="mb-0">Re-categorisation of Client Classification</h4>
                  <small class="text-muted">Please tick the option that applies to your request</small>
                </div>
              </div>
              <b-card-body>
              <fieldset :disabled="signed">
                <b-form-group>
                  <b-form-radio-group v-model="form.sectionF.classification" stacked>
                    <b-form-radio value="Professional">
                      We wish to be classified as a per se 'Professional Client' or an elective 'Professional Client'
                    </b-form-radio>
                    <b-form-radio value="Retail">
                      We request to be re-classified as a Retail Client instead of a Professional Client
                      (increasing the level of protection; note: we may not be able to do business with you if you
                      wish to be treated as a Retail Client)
                    </b-form-radio>
                    <b-form-radio value="EligibleCounterparty">
                      We request to be re-classified as an Eligible Counterparty instead of a Professional Client
                      (decreasing the level of protection)
                    </b-form-radio>
                  </b-form-radio-group>
                </b-form-group>

                <div class="border rounded p-1">
                  <p class="mb-50">
                    <strong>Requirements for reclassification from a Retail Client to a Professional Client</strong><br />
                    <small class="text-muted">
                      Should you wish to be reclassified as Professional Client then you must meet at least two (2)
                      of the following criteria:
                    </small>
                  </p>
                  <b-form-checkbox v-model="form.sectionF.criteria.transactions" class="mb-25">
                    You have made transactions of a significant size in the relevant market 10 times per quarter
                    in average during the four preceding quarters
                  </b-form-checkbox>
                  <b-form-checkbox v-model="form.sectionF.criteria.portfolio" class="mb-25">
                    The size of your financial portfolio (cash deposits and financial instruments) exceeds an
                    amount equivalent to EUR 500,000
                  </b-form-checkbox>
                  <b-form-checkbox v-model="form.sectionF.criteria.professional">
                    You work, or have worked, in the financial sector for at least one year in a professional
                    position which requires knowledge of the relevant transactions and investment services
                  </b-form-checkbox>
                </div>
              </fieldset>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION G ============================ -->
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">G</span>
                <div>
                  <h4 class="mb-0">Appointed Traders' Knowledge and Experience</h4>
                  <small class="text-muted">Each appointed trader should complete the following information</small>
                </div>
              </div>
              <b-card-body>

              <b-card
                v-for="(trader, index) in form.sectionG"
                :key="'traderG' + index"
                :title="'Appointed Trader ' + (index + 1)"
                class="trader-card mb-1"
              >
                <fieldset :disabled="signed">
                  <b-row>
                    <b-col cols="12" md="6">
                      <b-form-group label="Appointed trader name">
                        <b-form-input v-model="trader.name" />
                      </b-form-group>
                    </b-col>
                    <b-col cols="12" md="6">
                      <b-form-group label="Email">
                        <b-form-input v-model="trader.email" />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="6" md="3">
                      <b-form-group label="Mobile">
                        <b-form-input v-model="trader.mobile" />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6" md="3">
                      <b-form-group label="Tel / Home">
                        <b-form-input v-model="trader.telHome" />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6" md="3">
                      <b-form-group label="Tel / Office">
                        <b-form-input v-model="trader.telOffice" />
                      </b-form-group>
                    </b-col>
                    <b-col cols="6" md="3">
                      <b-form-group label="Fax">
                        <b-form-input v-model="trader.fax" />
                      </b-form-group>
                    </b-col>
                  </b-row>
                  <b-form-group label="1. Years of experience in the markets in which you wish to trade">
                    <b-form-input v-model="trader.yearsExperience" />
                  </b-form-group>
                  <b-form-group label="2. Overall frequency of your dealings in these markets">
                    <b-form-input v-model="trader.dealingFrequency" />
                  </b-form-group>
                  <b-form-group label="3. Typical size of transactions initiated by yourself">
                    <b-form-input v-model="trader.typicalSize" />
                  </b-form-group>
                  <b-form-group label="4. In what capacity have you been active in these markets?">
                    <b-form-radio-group
                      v-model="trader.capacity"
                      :options="['Advisory', 'Execution only', 'Discretion given to a third party']"
                    />
                  </b-form-group>
                  <b-form-group label="5. Are your trading activities performed on a long term or a speculative or hedging basis?">
                    <b-form-input v-model="trader.tradingBasis" />
                  </b-form-group>
                  <b-form-group label="6. Details of any other experience in any other financial market">
                    <b-form-textarea v-model="trader.otherExperience" rows="2" />
                  </b-form-group>
                  <b-form-group label="7. Do you understand the risks of dealing in these markets and the potential for loss of capital in excess of your original investment?">
                    <b-form-radio-group v-model="trader.understandsRisks" :options="['Yes', 'No']" />
                  </b-form-group>
                </fieldset>

                <div v-if="appointedTraderDocumentRules.length" class="person-docs mb-1">
                  <div
                    v-for="rule in appointedTraderDocumentRules"
                    :key="'agdoc' + index + '-' + rule.id"
                    class="doc-slot"
                  >
                    <div class="doc-slot-info">
                      <feather-icon
                        :icon="uploadedFor(rule.id, 'AppointedTrader', index) ? 'CheckCircleIcon' : 'CircleIcon'"
                        :class="uploadedFor(rule.id, 'AppointedTrader', index) ? 'text-success' : 'text-muted'"
                        size="14"
                        class="mr-50"
                      />
                      <span>{{ rule.Name }}</span>
                      <small v-if="uploadedFor(rule.id, 'AppointedTrader', index)" class="doc-slot-file">
                        {{ uploadedFor(rule.id, "AppointedTrader", index).FileName }}
                      </small>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="uploading"
                      @click="pickRuleFile(rule, 'AppointedTrader', index, trader.name)"
                    >
                      <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                      {{ uploadedFor(rule.id, "AppointedTrader", index) ? "Replace" : "Upload" }}
                    </b-button>
                  </div>
                </div>

                <b-form-group v-if="!signed" label="Trader signature">
                  <div class="signature-wrap">
                    <vue-signature :ref="'sigTrader' + index" h="160px" w="100%" />
                    <span class="signature-hint">Sign here</span>
                  </div>
                  <b-button variant="flat-secondary" size="sm" class="mt-25" @click="clearSignature('sigTrader' + index)">
                    Clear
                  </b-button>
                </b-form-group>

                <b-button
                  v-if="!signed"
                  variant="flat-danger"
                  size="sm"
                  @click="form.sectionG.splice(index, 1)"
                >
                  <feather-icon icon="Trash2Icon" size="14" class="mr-25" />
                  Remove this trader
                </b-button>
              </b-card>

              <b-button v-if="!signed" variant="flat-primary" @click="addTrader">
                <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                Add appointed trader
              </b-button>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION H ============================ -->
            <b-card no-body class="section-card">
              <div class="section-head">
                <span class="section-letter">H</span>
                <div>
                  <h4 class="mb-0">Additional Information</h4>
                  <small class="text-muted">Anything else you would like us to know</small>
                </div>
              </div>
              <b-card-body>
              <fieldset :disabled="signed">
                <b-form-textarea
                  v-model="form.sectionH.additionalInfo"
                  rows="4"
                  placeholder="Please use this space to supply any additional information you feel necessary."
                />
              </fieldset>
              </b-card-body>
            </b-card>

            <!-- ============================ SECTION E + F SIGNATURES ============================ -->
            <b-card v-if="!signed" no-body class="section-card section-card-sign">
              <div class="section-head">
                <span class="section-letter">E</span>
                <div>
                  <h4 class="mb-0">Client Declaration & Signatures</h4>
                  <small class="text-muted">Please review, sign and submit your agreement</small>
                </div>
              </div>
              <b-card-body>
              <p>I/We have:</p>
              <ul>
                <li>Read and understood the 'Loss of Protection Notice for Professional Clients' (Section C);</li>
                <li>Accurately completed the 'Client Information' (Section A) and will provide all identification evidence required;</li>
                <li>Accurately completed the 'Knowledge and Experience Information' (Section B);</li>
                <li>Read and understood the Terms of Business (Section D);</li>
                <li>Read and understood the Conflict of Interest Policy and the Best Execution Policy set out in the Terms of Business (Section D).</li>
              </ul>
              <p>
                By signing this Client Declaration I/we agree to the terms set out in Sections C and D,
                including the Conflict of Interest Policy and the Best Execution Policy.
              </p>

              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Name (only by an authorised signatory) *">
                    <validation-provider #default="{ errors }" rules="required" name="Declaration Name">
                      <b-form-input v-model="form.sectionE.name" :state="errors.length ? false : null" />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Signature *">
                <div class="signature-wrap">
                  <vue-signature ref="sigDeclaration" h="180px" w="100%" />
                  <span class="signature-hint">Sign here</span>
                </div>
                <b-button variant="flat-secondary" size="sm" class="mt-25" @click="clearSignature('sigDeclaration')">
                  Clear
                </b-button>
              </b-form-group>

              <hr />

              <h5>Section F — Re-categorisation signature</h5>
              <b-row>
                <b-col cols="12" md="6">
                  <b-form-group label="Name (only by an authorised signatory)">
                    <b-form-input v-model="form.sectionF.name" />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-form-group label="Signature">
                <div class="signature-wrap">
                  <vue-signature ref="sigRecat" h="180px" w="100%" />
                  <span class="signature-hint">Sign here</span>
                </div>
                <b-button variant="flat-secondary" size="sm" class="mt-25" @click="clearSignature('sigRecat')">
                  Clear
                </b-button>
              </b-form-group>
              </b-card-body>
            </b-card>

            <!-- ============================ ACTIONS ============================ -->
            <div v-if="!signed" class="action-bar card">
              <div class="card-body d-flex flex-wrap align-items-center">
                <b-button variant="outline-primary" class="mr-1" :disabled="saving" @click="saveProgress">
                  <b-spinner v-if="saving" small class="mr-50" />
                  <feather-icon v-else icon="SaveIcon" size="14" class="mr-50" />
                  Save Progress
                </b-button>
                <b-button variant="success" class="mr-1 btn-sign" :disabled="signing" @click="signAndSubmit">
                  <b-spinner v-if="signing" small class="mr-50" />
                  <feather-icon v-else icon="PenToolIcon" size="14" class="mr-50" />
                  Sign &amp; Submit
                </b-button>
                <span class="text-muted small flex-grow-1 mt-50 mt-md-0">
                  You can save your progress and come back later using the same link.
                  Once signed, the form can no longer be changed.
                </span>
              </div>
            </div>
          </validation-observer>
        </div>

        <!-- ============================ DOCUMENTS ============================ -->
        <b-card no-body class="section-card">
          <div class="section-head">
            <span class="section-letter section-letter-icon">
              <feather-icon icon="FileTextIcon" size="16" />
            </span>
            <div>
              <h4 class="mb-0">Required Documents</h4>
              <small class="text-muted">Documents requested by Swiss Finance Corporation</small>
            </div>
          </div>
          <b-card-body>
          <p v-if="requiredDocuments.length || accountDocumentRules.length" class="text-muted">
            Please upload the following documents requested by Swiss Finance Corporation.
          </p>
          <p v-else-if="documentRules.length && awaitingClientType" class="text-muted mb-0">
            Please choose your client type in Section A above and the documents we need from you
            will be listed here.
          </p>
          <p v-else class="text-muted mb-0">No documents have been requested from you yet.</p>

          <b-list-group v-if="accountDocumentRules.length" class="mb-1">
            <b-list-group-item
              v-for="rule in accountDocumentRules"
              :key="'rule' + rule.id"
              class="d-flex justify-content-between align-items-center flex-wrap"
            >
              <div>
                <feather-icon
                  :icon="uploadedFor(rule.id, 'Account', null) ? 'CheckCircleIcon' : 'CircleIcon'"
                  :class="uploadedFor(rule.id, 'Account', null) ? 'text-success' : 'text-muted'"
                  class="mr-50"
                />
                <strong>{{ rule.Name }}</strong>
                <small v-if="uploadedFor(rule.id, 'Account', null)" class="text-muted d-block ml-2">
                  {{ uploadedFor(rule.id, "Account", null).FileName }}
                </small>
              </div>
              <div>
                <b-badge :variant="uploadedFor(rule.id, 'Account', null) ? 'success' : 'warning'" class="mr-1">
                  {{ uploadedFor(rule.id, "Account", null) ? "Uploaded" : "Pending" }}
                </b-badge>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="uploading"
                  @click="pickRuleFile(rule, 'Account', null, null)"
                >
                  <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                  {{ uploadedFor(rule.id, "Account", null) ? "Replace" : "Upload" }}
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>

          <b-list-group v-if="requiredDocuments.length">
            <b-list-group-item
              v-for="required in requiredDocuments"
              :key="required.id"
              class="d-flex justify-content-between align-items-center flex-wrap"
            >
              <div>
                <feather-icon
                  :icon="required.Status === 'Uploaded' ? 'CheckCircleIcon' : 'CircleIcon'"
                  :class="required.Status === 'Uploaded' ? 'text-success' : 'text-muted'"
                  class="mr-50"
                />
                <strong>{{ required.TypeName }}</strong>
                <small v-if="required.Note" class="text-muted d-block ml-2">{{ required.Note }}</small>
              </div>
              <div>
                <b-badge :variant="required.Status === 'Uploaded' ? 'success' : 'warning'" class="mr-1">
                  {{ required.Status }}
                </b-badge>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="uploading"
                  @click="pickFile(required.id)"
                >
                  <feather-icon icon="UploadIcon" size="14" class="mr-25" />
                  Upload
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>

          <input ref="fileInput" type="file" class="d-none" @input="fileSelected" />

          <div class="mt-1">
            <b-button variant="outline-secondary" size="sm" :disabled="uploading" @click="pickFile(null)">
              <b-spinner v-if="uploading" small class="mr-25" />
              <feather-icon v-else icon="UploadIcon" size="14" class="mr-25" />
              Upload another document
            </b-button>
          </div>
          </b-card-body>
        </b-card>

        <b-card no-body class="section-card">
          <div class="section-head">
            <span class="section-letter section-letter-icon">
              <feather-icon icon="FolderIcon" size="16" />
            </span>
            <div>
              <h4 class="mb-0">Documents</h4>
              <small class="text-muted">Documents you have uploaded and documents shared with you</small>
            </div>
          </div>
          <b-card-body>
          <b-table
            :items="documents"
            :fields="documentFields"
            show-empty
            empty-text="No documents yet."
            responsive
          >
            <template #cell(Source)="{ value }">
              <b-badge :variant="value === 'Client' ? 'light-primary' : 'light-info'">
                {{ value === 'Client' ? 'Uploaded by you' : 'Swiss Finance' }}
              </b-badge>
            </template>
            <template #cell(FileSize)="{ value }">{{ formatSize(value) }}</template>
            <template #cell(actions)="{ item }">
              <b-button variant="flat-primary" size="sm" @click="downloadDocument(item)">
                <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
                Download
              </b-button>
            </template>
          </b-table>
          </b-card-body>
        </b-card>

        <div class="page-footer text-center">
          <img src="/logo.png" alt="Swiss Finance Corporation" class="footer-logo mb-50" />
          <p class="text-muted small mb-0">
            Swiss Finance Corporation Limited — 33 Cavendish Square, London W1G 0PW, United Kingdom<br />
            Authorised and regulated by the Financial Conduct Authority
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { required, email } from "@validations";
import vueSignature from "vue-signature";
import axiosIns from "@/libs/axios";
import { sectionCText, sectionDText } from "./pcaTexts";

const publicAxios = axios.create({ baseURL: axiosIns.defaults.baseURL });

function emptyTrader() {
  return {
    name: "",
    email: "",
    mobile: "",
    telHome: "",
    telOffice: "",
    fax: "",
    yearsExperience: "",
    dealingFrequency: "",
    typicalSize: "",
    capacity: null,
    tradingBasis: "",
    otherExperience: "",
    understandsRisks: null,
  };
}

function emptyForm() {
  return {
    sectionA: {
      accountName: "",
      clientType: null,
      address: "",
      contractsNameAddress: "",
      reportFrequency: "",
      reportForm: null,
      reportFormOther: "",
      companyRegNumber: "",
      taxCountry: "",
      natureOfBusiness: "",
      bankersNameAddress: "",
      telHome: "",
      telOffice: "",
      mobile: "",
      fax: "",
      email: "",
      sortCodeSwift: "",
      bankAccountName: "",
      bankAccountNumber: "",
      officerName: "",
      authorisedTraders: [{ name: "" }],
      nonDealingSignatories: [{ name: "" }],
    },
    sectionB: {
      yearsExperience: "",
      dealingFrequency: "",
      typicalSize: "",
      capacity: null,
      tradingBasis: "",
      otherExperience: "",
      understandsRisks: null,
    },
    sectionC: { acknowledged: false },
    sectionD: { acknowledged: false },
    sectionE: { name: "" },
    sectionF: {
      classification: "Professional",
      criteria: { transactions: false, portfolio: false, professional: false },
      name: "",
    },
    sectionG: [],
    sectionH: { additionalInfo: "" },
  };
}

export default {
  name: "ClientOnboarding",

  components: {
    ValidationObserver,
    ValidationProvider,
    vueSignature,
  },

  data() {
    return {
      loading: true,
      notFound: false,
      saving: false,
      signing: false,
      uploading: false,
      saveError: null,

      contactName: "",
      accountName: "",
      accountNumber: "",
      signed: false,
      signedDate: null,
      signatureName: null,

      form: emptyForm(),
      requiredDocuments: [],
      documentRules: [],
      allowedClientTypes: null,
      documents: [],

      pendingRequiredDocId: null,
      pendingRule: null,

      sectionCText,
      sectionDText,

      documentFields: [
        { key: "FileName", label: "File" },
        { key: "Source", label: "Source" },
        { key: "FileSize", label: "Size" },
        { key: "created_at", label: "Uploaded At" },
        { key: "actions", label: "" },
      ],
    };
  },

  computed: {
    token() {
      return this.$route.params.token;
    },

    // The account's holder type says which client types it can be. Where it allows the
    // choice made on the form, that narrows things further; where it does not, the holder
    // type wins, since that is the record Swiss Finance maintains.
    effectiveClientTypes() {
      const picked = this.form.sectionA.clientType;
      const allowed = this.allowedClientTypes;

      if (allowed === null || allowed === undefined) return picked ? [picked] : [];
      if (!allowed.length) return [];
      if (picked && allowed.indexOf(picked) !== -1) return [picked];
      return allowed;
    },

    // Necessary documents are configured in the settings and already filtered by the
    // settlement type of the account.
    matchingDocumentRules() {
      const clientTypes = this.effectiveClientTypes;
      if (!clientTypes.length) return [];

      return this.documentRules.filter((rule) => {
        const types = rule.ClientTypes;
        if (!types || !types.length) return true; // no restriction → applies to all
        return types.some((type) => clientTypes.indexOf(type) !== -1);
      });
    },

    // Section B fields are only mandatory for non-Individual accounts.
    sectionBRules() {
      return this.form.sectionA.clientType === 'Individual' ? '' : 'required';
    },

    // True only while we are still waiting for the client to say what they are.
    awaitingClientType() {
      return (
        (this.allowedClientTypes === null || this.allowedClientTypes === undefined) &&
        !this.form.sectionA.clientType
      );
    },

    accountDocumentRules() {
      return this.matchingDocumentRules.filter(
        (rule) => !rule.PerAuthorisedTrader && !rule.PerNonDealingSignatory && !rule.PerAppointedTrader
      );
    },

    authorisedTraderDocumentRules() {
      return this.matchingDocumentRules.filter((rule) => rule.PerAuthorisedTrader);
    },

    signatoryDocumentRules() {
      return this.matchingDocumentRules.filter((rule) => rule.PerNonDealingSignatory);
    },

    appointedTraderDocumentRules() {
      return this.matchingDocumentRules.filter((rule) => rule.PerAppointedTrader);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    load() {
      this.loading = true;
      publicAxios
        .get(`client-onboarding/${this.token}`)
        .then((res) => {
          const data = res.data;
          this.contactName = data.contactName;
          this.accountName = data.accountName;
          this.accountNumber = data.accountNumber;
          this.signed = data.signed;
          this.signedDate = data.signedDate;
          this.signatureName = data.signatureName;
          this.requiredDocuments = data.requiredDocuments || [];
          this.documentRules = data.documentRules || [];
          this.allowedClientTypes = data.allowedClientTypes;
          this.documents = data.documents || [];

          if (data.formData) {
            // merge saved data over the empty structure so new fields keep defaults
            const base = emptyForm();
            this.form = {
              ...base,
              ...data.formData,
              sectionA: { ...base.sectionA, ...(data.formData.sectionA || {}) },
              sectionB: { ...base.sectionB, ...(data.formData.sectionB || {}) },
              sectionC: { ...base.sectionC, ...(data.formData.sectionC || {}) },
              sectionD: { ...base.sectionD, ...(data.formData.sectionD || {}) },
              sectionE: { ...base.sectionE, ...(data.formData.sectionE || {}) },
              sectionF: {
                ...base.sectionF,
                ...(data.formData.sectionF || {}),
                criteria: { ...base.sectionF.criteria, ...((data.formData.sectionF || {}).criteria || {}) },
              },
              sectionG: data.formData.sectionG || [],
              sectionH: { ...base.sectionH, ...(data.formData.sectionH || {}) },
            };
          } else if (this.accountName) {
            this.form.sectionA.accountName = this.accountName;
          }
        })
        .catch(() => {
          this.notFound = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    refreshDocuments() {
      return publicAxios.get(`client-onboarding/${this.token}`).then((res) => {
        this.requiredDocuments = res.data.requiredDocuments || [];
        this.documentRules = res.data.documentRules || [];
        this.allowedClientTypes = res.data.allowedClientTypes;
        this.documents = res.data.documents || [];
      });
    },

    // Most recent upload satisfying a necessary document for a given person (null for
    // account-wide documents). `documents` arrives newest first.
    uploadedFor(ruleId, scope, personIndex) {
      return (
        this.documents.find(
          (document) =>
            document.DocumentTypeId === ruleId &&
            document.Scope === scope &&
            (personIndex === null
              ? document.PersonIndex === null || document.PersonIndex === undefined
              : document.PersonIndex === personIndex)
        ) || null
      );
    },

    addTrader() {
      this.form.sectionG.push(emptyTrader());
    },

    clearSignature(refName) {
      const pad = this.getSignaturePad(refName);
      if (pad) pad.clear();
    },

    getSignaturePad(refName) {
      const ref = this.$refs[refName];
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },

    signatureData(refName) {
      const pad = this.getSignaturePad(refName);
      if (!pad || pad.isEmpty()) return null;
      return pad.save("image/png");
    },

    saveProgress() {
      this.saving = true;
      this.saveError = null;
      publicAxios
        .post(`client-onboarding/${this.token}/saveForm`, { formData: this.form })
        .then(() => {
          this.$bvToast.toast("Your progress has been saved. You can return using the same link.", {
            title: "Saved",
            variant: "success",
            solid: true,
          });
        })
        .catch((err) => {
          this.saveError = (err.response && err.response.data && err.response.data.message) || "Saving failed. Please try again.";
        })
        .finally(() => {
          this.saving = false;
        });
    },

    signAndSubmit() {
      this.saveError = null;

      this.$refs.formValidation.validate().then((valid) => {
        if (!valid) {
          this.saveError = "Please complete all required fields before signing.";
          return;
        }

        if (!this.form.sectionC.acknowledged || !this.form.sectionD.acknowledged) {
          this.saveError =
            "Please confirm that you have read and understood the Loss of Protection Notice (Section C) and the Terms of Business (Section D).";
          return;
        }

        const signatureImage = this.signatureData("sigDeclaration");
        if (!signatureImage) {
          this.saveError = "Please draw your signature in the Client Declaration (Section E).";
          return;
        }

        if (this.form.sectionF.classification === "Retail") {
          const criteria = this.form.sectionF.criteria;
          const ticked = [criteria.transactions, criteria.portfolio, criteria.professional].filter(Boolean).length;
          if (ticked > 0 && ticked < 2) {
            this.saveError = "Please tick at least two reclassification criteria in Section F, or none.";
            return;
          }
        }

        // collect per-trader signatures as base64 inside the form payload
        const formData = JSON.parse(JSON.stringify(this.form));
        formData.sectionG.forEach((trader, index) => {
          const data = this.signatureData("sigTrader" + index);
          if (data) trader.signatureImage = data;
        });

        this.signing = true;
        publicAxios
          .post(`client-onboarding/${this.token}/signForm`, {
            formData,
            signatureName: this.form.sectionE.name,
            signatureImage,
            recatSignatureName: this.form.sectionF.name || null,
            recatSignatureImage: this.signatureData("sigRecat"),
          })
          .then(() => {
            this.$bvToast.toast("Thank you. The form has been signed and submitted.", {
              title: "Signed",
              variant: "success",
              solid: true,
            });
            this.load();
          })
          .catch((err) => {
            this.saveError =
              (err.response && err.response.data && err.response.data.message) || "Signing failed. Please try again.";
          })
          .finally(() => {
            this.signing = false;
          });
      });
    },

    pickFile(requiredDocumentId) {
      this.pendingRequiredDocId = requiredDocumentId;
      this.pendingRule = null;
      this.$refs.fileInput.click();
    },

    pickRuleFile(rule, scope, personIndex, personName) {
      this.pendingRequiredDocId = null;
      this.pendingRule = { id: rule.id, scope, personIndex, personName: personName || null };
      this.$refs.fileInput.click();
    },

    fileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploading = true;
      const formData = new FormData();
      formData.append("file", file);
      if (this.pendingRequiredDocId) {
        formData.append("requiredDocumentId", this.pendingRequiredDocId);
      }
      if (this.pendingRule) {
        formData.append("documentTypeId", this.pendingRule.id);
        formData.append("scope", this.pendingRule.scope);
        if (this.pendingRule.personIndex !== null) {
          formData.append("personIndex", this.pendingRule.personIndex);
          formData.append("personName", this.pendingRule.personName || "");
        }
      }

      publicAxios
        .post(`client-onboarding/${this.token}/uploadDocument`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then(() => {
          this.$bvToast.toast("Document uploaded. Thank you.", { title: "Uploaded", variant: "success", solid: true });
          return this.refreshDocuments();
        })
        .catch((err) => {
          this.$bvToast.toast(
            (err.response && err.response.data && err.response.data.message) || "Upload failed. Please try again.",
            { title: "Error", variant: "danger", solid: true }
          );
        })
        .finally(() => {
          this.uploading = false;
          this.$refs.fileInput.value = "";
          this.pendingRequiredDocId = null;
          this.pendingRule = null;
        });
    },

    downloadDocument(item) {
      publicAxios
        .get(`client-onboarding/${this.token}/downloadDocument/${item.id}`, { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          const link = document.createElement("a");
          link.href = url;
          link.download = item.FileName;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$bvToast.toast("Download failed.", { title: "Error", variant: "danger", solid: true });
        });
    },

    formatSize(bytes) {
      if (!bytes) return "—";
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style lang="scss">
.client-onboarding-page {
  $ink: #2f3a52; // slate matching the grayscale SFC wordmark
  $ink-light: #6b7691;
  $accent: #b8933f; // understated gold accent
  $page-bg-1: #f5f7fb;
  $page-bg-2: #e9edf5;

  min-height: 100vh;
  background: linear-gradient(180deg, $page-bg-1 0%, $page-bg-2 100%);
  color: $ink;
  font-size: 1.1rem;

  .container {
    max-width: 920px;
  }

  /* ---------- cards ---------- */
  .card {
    border: none;
    border-radius: 14px;
    box-shadow: 0 2px 14px rgba(30, 40, 70, 0.07);
  }

  .section-card {
    margin-bottom: 1.5rem;
    overflow: hidden;
  }

  /* ---------- hero ---------- */
  .hero-card {
    overflow: hidden;

    .hero-accent {
      height: 5px;
      background: linear-gradient(90deg, $ink 0%, lighten($ink, 25%) 60%, $accent 100%);
    }

    .hero-logo {
      height: 42px;
      max-width: 90%;
      object-fit: contain;
    }

    .hero-title {
      color: $ink-light;
      font-weight: 400;
      letter-spacing: 3px;
      text-transform: uppercase;
      font-size: 1.05rem;
    }

    .hero-chips {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 0.5rem;
    }

    .hero-chip {
      display: inline-flex;
      align-items: center;
      padding: 0.35rem 0.85rem;
      border-radius: 50rem;
      background: $page-bg-1;
      border: 1px solid darken($page-bg-2, 4%);
      font-size: 0.85rem;
      font-weight: 500;
      color: $ink;

      .chip-muted {
        color: $ink-light;
        font-weight: 400;
      }

      &.chip-success {
        background: #e8f6ee;
        border-color: #cdebd9;
        color: #1f7a4d;
      }

      &.chip-pending {
        background: #fdf6e7;
        border-color: #f3e5c4;
        color: darken($accent, 8%);
      }
    }
  }

  /* ---------- section headers ---------- */
  .section-head {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.15rem 1.5rem;
    background: linear-gradient(180deg, #fbfcfe 0%, #f4f6fb 100%);
    border-bottom: 1px solid #eceff6;

    h4 {
      color: $ink;
      font-weight: 700;
      font-size: 1.2rem;
    }
  }

  .section-letter {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: $ink;
    color: #fff;
    font-weight: 700;
    font-size: 1.05rem;
    box-shadow: 0 3px 8px rgba(47, 58, 82, 0.28);
  }

  .section-card-sign .section-letter {
    background: $accent;
    box-shadow: 0 3px 8px rgba(184, 147, 63, 0.35);
  }

  /* ---------- form polish ---------- */
  legend,
  label {
    color: $ink;
    font-weight: 600;
    font-size: 1.05rem;
  }

  .form-control,
  .custom-select {
    border-radius: 8px;
    font-size: 1.05rem;

    &:focus {
      border-color: lighten($ink, 30%);
      box-shadow: 0 0 0 3px rgba(47, 58, 82, 0.08);
    }
  }

  h6 {
    color: $ink;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.9rem;
    margin-top: 1.25rem;
  }

  fieldset:disabled {
    opacity: 0.7;
  }

  /* ---------- per-person required documents ---------- */
  .person-block {
    margin-bottom: 0.75rem;
  }

  .person-docs {
    margin: 0 0 0.5rem 0.25rem;
    padding-left: 0.9rem;
    border-left: 3px solid #e3e8f2;
  }

  .doc-slot {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.35rem;
    background: #fbfcfe;
    border: 1px solid #eceff6;
    border-radius: 8px;
    font-size: 0.95rem;

    .doc-slot-info {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.25rem;
    }

    .doc-slot-file {
      color: $ink-light;
      margin-left: 0.5rem;
    }
  }

  .trader-card {
    border: 1px solid #eceff6 !important;
    border-radius: 10px;
    box-shadow: none;
    background: #fbfcfe;
  }

  /* ---------- notices ---------- */
  .notice-text {
    white-space: pre-wrap;
    max-height: 320px;
    overflow-y: auto;
    font-size: 1rem;
    line-height: 1.65;
    color: lighten($ink, 12%);
    background: #fafbfd;
    border: 1px solid #eceff6;
    border-radius: 10px;
    padding: 1.25rem 1.5rem;
  }

  .ack-check {
    font-weight: 500;
    color: $ink;
  }

  /* ---------- signatures ---------- */
  .signature-wrap {
    position: relative;
    background: #fff;
    border: 2px dashed #cdd4e2;
    border-radius: 10px;
    overflow: hidden;
    transition: border-color 0.2s;

    &:hover {
      border-color: $accent;
    }

    .signature-hint {
      position: absolute;
      bottom: 6px;
      right: 12px;
      font-size: 0.7rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: #c3cad8;
      pointer-events: none;
    }
  }

  /* ---------- action bar ---------- */
  .action-bar {
    position: sticky;
    bottom: 1rem;
    z-index: 10;
    border: 1px solid #eceff6;
    box-shadow: 0 8px 28px rgba(30, 40, 70, 0.16);

    .btn-sign {
      background: $accent !important;
      border-color: $accent !important;

      &:hover {
        background: darken($accent, 6%) !important;
      }
    }
  }

  /* ---------- documents ---------- */
  .list-group-item {
    border-color: #eceff6;
    padding: 1rem 1.25rem;

    &:first-child {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }

    &:last-child {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }
  }

  /* ---------- footer ---------- */
  .page-footer {
    padding: 1.5rem 0 2rem;

    .footer-logo {
      height: 22px;
      opacity: 0.55;
      filter: grayscale(1);
    }
  }
}
</style>
